<script setup>
import { computed } from 'vue';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import ContactSidebarResizeHandle from 'dashboard/components/ContactSidebarResizeHandle.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
  sidebarWidth: {
    type: Number,
    default: null,
  },
  isDraggingSidebar: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['startSidebarDrag', 'resetSidebarWidth']);

const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();

const activeTab = computed(() => {
  const { is_contact_sidebar_open: isContactSidebarOpen } = uiSettings.value;

  if (isContactSidebarOpen) {
    return 0;
  }
  return null;
});

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const closeContactPanel = () => {
  if (isSmallScreen.value && uiSettings.value?.is_contact_sidebar_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
  }
};

// Desktop only: apply custom width via inline style when user has resized
const sidebarStyle = computed(() => {
  if (isSmallScreen.value || props.sidebarWidth == null) {
    return {};
  }
  return {
    width: props.sidebarWidth + 'px',
    minWidth: props.sidebarWidth + 'px',
  };
});

// When sidebarWidth is set, remove the Tailwind width classes to avoid conflicts
const hasSidebarWidth = computed(
  () => !isSmallScreen.value && props.sidebarWidth != null
);
</script>

<template>
  <div
    v-on-click-outside="() => closeContactPanel()"
    class="bg-n-background h-full overflow-hidden flex flex-col fixed top-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out ltr:right-0 rtl:left-0 md:relative ltr:border-l rtl:border-r border-n-weak shadow-lg md:shadow-none"
    :class="[
      {
        'md:flex': activeTab === 0,
        'md:hidden': activeTab !== 0,
        'md:w-[320px] md:min-w-[320px] 2xl:min-w-[360px] 2xl:w-[360px]':
          !hasSidebarWidth,
      },
    ]"
    :style="sidebarStyle"
  >
    <!-- Desktop-only resize handle on the left edge -->
    <ContactSidebarResizeHandle
      v-if="!isSmallScreen"
      :is-dragging="isDraggingSidebar"
      @start-drag="$emit('startSidebarDrag', $event)"
      @reset-width="$emit('resetSidebarWidth')"
    />
    <div class="flex flex-1 overflow-auto">
      <ContactPanel
        v-show="activeTab === 0"
        :conversation-id="currentChat.id"
        :inbox-id="currentChat.inbox_id"
      />
    </div>
  </div>
</template>
